import { CssBaseline } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enGB } from 'date-fns/locale/en-GB';

import settings from '@/constants/constants';
import { PreferencesProvider } from '@montugroup/circuit-shared/context/Preferences';

import './services/authentication.service.patch'; // Required to correctly configure customer portal specific auth

import { useSession, useSessionSync } from '@montugroup/circuit-shared';
import { CircularProgressWrapper } from '@montugroup/circuit-shared/components/common/CircularProgressWrapper';
import { BrazeContentCardContext } from '@montugroup/circuit-shared/context/braze/BrazeContentCardContext';
import { useNavigateSetup } from '@montugroup/circuit-shared/hooks/useNavigateSetup';
import '@montugroup/circuit-shared/utils/polyfills';
import { MapViewProvider } from '@montugroup/design-system/MapView';
import { useEffect } from 'react';
import AlertSnackbarProvider from './components/error/AlertSnackbarProvider';
import { ErrorManagementProvider } from './context/ErrorManagement';
import { PatientReferralProvider } from './context/PatientReferral';
import { useAxiosSetup } from './hooks/useAxiosSetup';
import { useBraze } from './hooks/useBraze';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { usePortalRedirector } from './hooks/usePortalRedirector';
import useCustomer from './hooks/user/useCustomer';
import { useSetDatadogUser } from './hooks/useSetDatadogUser';
import useTrackingProviders from './hooks/useTrackingProviders';
import { useUserFeatureFlagsSync } from './hooks/useUserFeatureFlagsSync';
import Maintenance from './pages/maintenance/Maintenance';
import { AuthenticatedSnowplow } from './providers/authenticated-snowplow';
import { SnowplowUIIntegration } from './providers/snowplow-ui-integration';
import Routes from './routes/routes';
import { Logger } from './utils/logger';

const logger = new Logger('App');

function App() {
  useNavigateSetup();
  useAxiosSetup();
  useSetDatadogUser();
  const shouldShowMaintenanceScreen = useFeatureFlag('show_maintenance_screen');
  const shouldUseSessionStorageForAuth = useFeatureFlag('auth_enable_session_storage');
  const isSessionSyncEnabled = useFeatureFlag('auth_enable_cross_tab_session_sync');
  const { storageType, setStorageType } = useSession();
  const { loading: loadingUserFlags } = useUserFeatureFlagsSync();
  const { isPendingRedirection } = usePortalRedirector();
  const { isPendingSessionSync } = useSessionSync({
    isFlagReady: !isPendingRedirection && !loadingUserFlags,
    isFlagEnabled: shouldUseSessionStorageForAuth && isSessionSyncEnabled,
  });
  const { isLoading: customerIsLoading } = useCustomer();
  const { brazeContentCards } = useBraze();
  useTrackingProviders({ ready: !loadingUserFlags });

  // TODO: remove useEffect after sessionStorage migration is done
  useEffect(() => {
    if (storageType !== 'sessionStorage' && shouldUseSessionStorageForAuth === true) {
      setStorageType('sessionStorage');
    } else if (storageType !== 'localStorage' && shouldUseSessionStorageForAuth === false) {
      setStorageType('localStorage');
    }
  }, [setStorageType, shouldUseSessionStorageForAuth, storageType]);

  // NOTE: We are showing customer query loading here to prevent the need for several global loading spinners that jump/glitch to different progress points
  if (loadingUserFlags || isPendingRedirection || isPendingSessionSync || customerIsLoading) {
    logger.info('loadingSpinnerHit', {
      loadingUserFlags,
      isPendingRedirection,
      isPendingSessionSync,
      customerIsLoading,
    });

    return <CircularProgressWrapper />;
  }

  logger.info('notLoading');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <MapViewProvider apiKey={settings.googlePlacesApiKey}>
        <BrazeContentCardContext.Provider value={brazeContentCards}>
          <CssBaseline />
          <AlertSnackbarProvider>
            <ErrorManagementProvider initialErrors={[]}>
              {shouldShowMaintenanceScreen ? (
                <Maintenance />
              ) : (
                <PreferencesProvider>
                  <PatientReferralProvider>
                    <AuthenticatedSnowplow>
                      <SnowplowUIIntegration>
                        <Routes />
                      </SnowplowUIIntegration>
                    </AuthenticatedSnowplow>
                  </PatientReferralProvider>
                </PreferencesProvider>
              )}
            </ErrorManagementProvider>
          </AlertSnackbarProvider>
        </BrazeContentCardContext.Provider>
      </MapViewProvider>
    </LocalizationProvider>
  );
}

export default App;
